import { ReactNode, useRef } from 'react'

import { useVirtualizeRows } from '../../hooks/useVirtualizeRows'
import { astraTwMerge } from '../Utilities/astraTwMerge'
import { BodyRow, HeaderRow, Loader, SpacerRow, TableCell } from './components'
import { Id, RowType } from './types'
import { isDefined } from './utils'

export type ColumnSize = { minWidth?: string; maxWidth?: string }

export const cellHeightStyle = {
    short: 34,
    tall: 64,
} as const

export type CellHeight = keyof typeof cellHeightStyle

export type DataTableProps<TRow extends { id: Id }, TCol extends { id: Id }> = {
    cols: (TCol & ColumnSize)[]
    rows: TRow[]

    body: {
        rowClassName?: (row: TRow) => string

        cellRender: (row: TRow, col: TCol) => ReactNode
        cellClassName?: (row: TRow, col: TCol) => string
        actionButton?: (row: TRow, col: TCol) => ReactNode
    }

    headers?: (RowType<TCol> | null)[]
    footers?: (RowType<TCol> | null)[]
    id?: string
    fullWidthCells?: boolean
    cellDataTest?: string
    className?: string
    isLoading?: boolean
    'data-test'?: string

    /**
     * If set to valid value, the table virtualizes its body: only visible
     * rows (plus overscan) are rendered.
     * */
    optimizeRendering?: { cellHeight?: CellHeight }
}

export function DataTable<TRow extends { id: Id }, TCol extends { id: Id }>({
    id,
    cols,
    rows,
    body,
    headers,
    footers,
    cellDataTest,
    className,
    fullWidthCells,
    isLoading,
    optimizeRendering,
    'data-test': dataTest,
}: DataTableProps<TRow, TCol>) {
    const isCellFullWidth = fullWidthCells ? 'minmax(28px, 1fr)' : 'minmax(28px, 190px)'
    const gridTemplateColumns = cols.map(col => (col.maxWidth ? `minmax(28px, ${col.maxWidth})` : isCellFullWidth)).join(' ')

    const tableRef = useRef<HTMLTableElement>(null)
    const headerRef = useRef<HTMLTableSectionElement>(null)
    const footerRef = useRef<HTMLTableSectionElement>(null)

    const cellHeight = optimizeRendering?.cellHeight ? cellHeightStyle[optimizeRendering.cellHeight] : undefined

    const isVirtualized = cellHeight !== undefined
    const {
        start,
        end,
        pitch,
        ref: bodyRef,
    } = useVirtualizeRows({
        rowCount: rows.length,
        nominalRowHeight: cellHeight ?? 0,
        enabled: isVirtualized,
        rowSelector: '[data-test="body-row"]',
    })
    const visibleRows = isVirtualized ? rows.slice(start, end) : rows
    const topSpacerHeight = start * pitch
    const bottomSpacerHeight = (rows.length - end) * pitch

    return (
        <table
            ref={tableRef}
            id={id}
            data-test={dataTest}
            className={astraTwMerge(
                'border-tools-table-outline isolate flex size-full max-h-full w-full max-w-full table-fixed border-separate select-none flex-col overflow-auto rounded-lg border border-subtle bg-surface-base',
                className
            )}
        >
            <thead ref={headerRef} className="sticky top-0 z-priority grid whitespace-nowrap border-b border-subtle">
                {headers
                    ?.filter(isDefined)
                    .map(({ key, ...header }) => <HeaderRow key={key} cols={cols} gridTemplateColumns={gridTemplateColumns} {...header} />)}
            </thead>

            <tbody ref={bodyRef} className="thin-scroll overflow-y-auto">
                {!isLoading ? (
                    <>
                        {isVirtualized && topSpacerHeight > 0 && <SpacerRow height={topSpacerHeight} colSpan={cols.length} />}
                        {visibleRows.map(row => (
                            <BodyRow key={row.id} gridTemplateColumns={gridTemplateColumns} row={row} cols={cols} {...body}>
                                {column => (
                                    <TableCell
                                        key={`${row.id}-${column.id}`}
                                        col={column}
                                        row={row}
                                        data-test={cellDataTest}
                                        height={cellHeight}
                                        cellClassName={body.cellClassName}
                                        actionButton={body.actionButton}
                                    >
                                        {body.cellRender(row, column)}
                                    </TableCell>
                                )}
                            </BodyRow>
                        ))}
                        {isVirtualized && bottomSpacerHeight > 0 && <SpacerRow height={bottomSpacerHeight} colSpan={cols.length} />}
                    </>
                ) : (
                    <Loader cols={cols.length} />
                )}
            </tbody>
            <tfoot ref={footerRef} className={astraTwMerge('sticky bottom-0 grid whitespace-nowrap', Number(footers?.length) > 0 ? 'border-t-2' : '')}>
                {footers
                    ?.filter(isDefined)
                    .map(({ key, ...footer }) => <HeaderRow key={key} cols={cols} gridTemplateColumns={gridTemplateColumns} {...footer} />)}
            </tfoot>
        </table>
    )
}
